import { buildTitle } from "@/lib/seo-quality";
import { robotsMeta } from "@/lib/seo-quality";
import { assessPartGuide } from "@/lib/seo-families/part-guides";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, Clock, Wrench, AlertCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { FyndanuInline } from "@/components/FyndanuInline";
import { PageHeader } from "./fordon";
import { getPartGuide, PART_GUIDES, type PartGuide } from "@/data/rapido-part-guides";

export const Route = createFileRoute("/rapido/guider/$slug")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const guide = getPartGuide(params.slug);
    if (!guide) throw notFound();
    // robots räknas ut här så head() slipper importera guidedatan.
    return { guide, robots: robotsMeta(assessPartGuide(guide)) };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const g = loaderData.guide;
    const url = `https://www.akhusbilfritid.se/rapido/guider/${g.slug}`;
    return {
      meta: [
        ...loaderData.robots,
        { title: buildTitle(g.metaTitle, "AK Husbil & Fritid") },
        { name: "description", content: g.metaDesc },
        { name: "keywords", content: `${g.focusKeyword}, rapido reservdelar, ${g.system.toLowerCase()} husbil, felsök husbil, ${g.title.toLowerCase()}` },
        { property: "og:type", content: "article" },
        { property: "og:title", content: g.metaTitle },
        { property: "og:description", content: g.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: g.title,
            description: g.metaDesc,
            totalTime: g.timeEstimate,
            estimatedCost: { "@type": "MonetaryAmount", currency: "SEK", value: g.priceEstimate },
            tool: g.tools.map((t) => ({ "@type": "HowToTool", name: t })),
            step: g.diagnosticSteps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.title,
              text: s.detail,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: g.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se" },
              { "@type": "ListItem", position: 2, name: "Rapido", item: "https://www.akhusbilfritid.se/rapido" },
              { "@type": "ListItem", position: 3, name: g.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold mb-4">Guiden hittades inte</h1>
      <Link to="/rapido" className="text-primary underline">Till Rapido-sidan</Link>
    </div>
  ),
  component: GuidePage,
});

function GuidePage() {
  const data = Route.useLoaderData() as { guide: PartGuide };
  const g = data.guide;
  const related = g.relatedSlugs
    ? PART_GUIDES.filter((x) => g.relatedSlugs!.includes(x.slug))
    : PART_GUIDES.filter((x) => x.system === g.system && x.slug !== g.slug).slice(0, 3);

  return (
    <>
      <PageHeader label={g.system} title={g.title} sub={`Rapido-guide • ${g.timeEstimate} • ${g.priceEstimate}`} />

      <main className="max-w-4xl mx-auto px-4 py-10 space-y-10">
        <nav className="text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-2">/</span>
          <Link to="/rapido" className="hover:underline">Rapido</Link>
          <span className="mx-2">/</span>
          <span>{g.title}</span>
        </nav>

        {/* Snabbfakta */}
        <section className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-lg border p-4">
            <Wrench className="w-5 h-5 text-primary mb-2" />
            <div className="text-xs text-muted-foreground">System</div>
            <div className="font-semibold">{g.system}</div>
          </div>
          <div className="rounded-lg border p-4">
            <Clock className="w-5 h-5 text-primary mb-2" />
            <div className="text-xs text-muted-foreground">Tidsåtgång</div>
            <div className="font-semibold">{g.timeEstimate}</div>
          </div>
          <div className="rounded-lg border p-4">
            <AlertCircle className="w-5 h-5 text-primary mb-2" />
            <div className="text-xs text-muted-foreground">Prisintervall</div>
            <div className="font-semibold">{g.priceEstimate}</div>
          </div>
        </section>

        {/* Berörda modeller */}
        <section>
          <h2 className="text-lg font-semibold mb-3">Gäller följande Rapido-modeller</h2>
          <div className="flex flex-wrap gap-2">
            {g.models.map((m) => (
              <span key={m} className="px-3 py-1.5 rounded-full bg-muted text-sm">{m}</span>
            ))}
          </div>
        </section>

        {/* Symptom */}
        {g.symptoms.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-4">Symptom</h2>
            <ul className="space-y-2">
              {g.symptoms.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Orsaker */}
        {g.causes.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-4">Vanliga orsaker</h2>
            <ul className="space-y-2 list-disc pl-6">
              {g.causes.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Djupinnehåll */}
        {g.content.map((c, i) => (
          <section key={i}>
            <h2 className="text-2xl font-bold mb-4">{c.heading}</h2>
            <p className="leading-relaxed whitespace-pre-line">{c.body}</p>
          </section>
        ))}

        {/* Diagnostiksteg */}
        {g.diagnosticSteps.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-4">Diagnostiksteg</h2>
            <ol className="space-y-4">
              {g.diagnosticSteps.map((s, i) => (
                <li key={i} className="flex gap-4">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{s.title}</h3>
                    <p className="text-muted-foreground">{s.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Verktyg */}
        {g.tools.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-4">Verktyg som behövs</h2>
            <ul className="grid sm:grid-cols-2 gap-2">
              {g.tools.map((t, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2 rounded border">
                  <Wrench className="w-4 h-4 text-muted-foreground" />
                  {t}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* FAQ */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Vanliga frågor</h2>
          <div className="space-y-4">
            {g.faq.map((f, i) => (
              <div key={i} className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Behöver du hjälp?</h2>
          <p className="mb-6 opacity-90">Vi är officiell Rapido-återförsäljare och gör dessa jobb varje vecka. Ring för snabb hjälp, beställ reservdel eller kika på lagret om du letar en annan Rapido.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
              <Phone className="w-4 h-4" /> 070-862 97 43
            </a>
            <Link to="/reservdelar" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10">
              Beställ reservdel
            </Link>
            <a
              href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10"
            >
              Se Rapido i lager
            </a>
          </div>
        </section>

        {/* Relaterade */}
        {related.length > 0 && (
          <section>
            <h2 className="text-xl font-bold mb-4">Relaterade guider</h2>
            <div className="grid gap-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/rapido/guider/$slug"
                  params={{ slug: r.slug }}
                  className="flex items-center justify-between px-4 py-3 rounded-lg border hover:bg-muted transition"
                >
                  <div>
                    <div className="text-xs text-muted-foreground">{r.system}</div>
                    <div className="font-medium">{r.title}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 opacity-50 shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <FyndanuInline context={`${g.title} reservdel service ${g.system ?? ""}`} />

        <AdSlot variant="multiplex" label="Fler Rapido-guider" />


        <div className="pt-6 border-t">
          <Link to="/rapido" className="inline-flex items-center gap-2 text-primary hover:underline">
            <ArrowLeft className="w-4 h-4" /> Tillbaka till Rapido-portalen
          </Link>
        </div>
      </main>
    </>
  );
}
